// Production Safeguards — Fase 15.8
export type SafeguardModule = 'query' | 'broadcast' | 'realtime' | 'emergency' | 'runtime'

export interface RateLimitEntry {
  key: string; count: number; windowMs: number; maxCount: number; resetAt: number
}
export interface SafeguardViolation {
  timestamp: string; module: SafeguardModule; rule: string; value: number; threshold: number; blocked: boolean
}
export interface SafeguardSnapshot {
  timestamp: string; emergencyStop: boolean; rateLimits: RateLimitEntry[]
  violations: SafeguardViolation[]; broadcastThrottle: boolean; realtimeStormDetected: boolean
}

const QRL  = { windowMs: 10_000, maxCount: 50  }
const BCR  = { windowMs:  5_000, maxCount: 20  }
const RTS  = { windowMs:  1_000, maxCount: 100 }
const MAX_V = 200

let _emergencyStop         = false
let _broadcastThrottle     = false
let _realtimeStorm         = false
const _limits: Map<string, RateLimitEntry> = new Map()
const _violations: SafeguardViolation[]    = []

function checkLimit(key: string, windowMs: number, maxCount: number): { allowed: boolean; entry: RateLimitEntry } {
  const now = Date.now(); let e = _limits.get(key)
  if (!e || now > e.resetAt) { e = { key, count: 0, windowMs, maxCount, resetAt: now + windowMs }; _limits.set(key, e) }
  e.count++; return { allowed: e.count <= maxCount, entry: e }
}
function violation(module: SafeguardModule, rule: string, value: number, threshold: number, blocked: boolean): void {
  _violations.push({ timestamp: new Date().toISOString(), module, rule, value, threshold, blocked })
  if (_violations.length > MAX_V) _violations.splice(0, _violations.length - MAX_V)
}

export function checkQueryRateLimit(key = 'global'): boolean {
  if (_emergencyStop) return false
  const { allowed, entry } = checkLimit(`q:${key}`, QRL.windowMs, QRL.maxCount)
  if (!allowed) violation('query', 'rate_exceeded', entry.count, entry.maxCount, true)
  return allowed
}
export function checkBroadcastAllowed(key = 'global'): boolean {
  if (_emergencyStop || _broadcastThrottle) return false
  const { allowed, entry } = checkLimit(`bc:${key}`, BCR.windowMs, BCR.maxCount)
  if (!allowed) {
    _broadcastThrottle = true; violation('broadcast', 'throttle_triggered', entry.count, entry.maxCount, true)
    setTimeout(() => { _broadcastThrottle = false }, BCR.windowMs)
  }
  return allowed
}
export function checkRealtimeEvent(key = 'global'): boolean {
  if (_emergencyStop) return false
  const { allowed, entry } = checkLimit(`rt:${key}`, RTS.windowMs, RTS.maxCount)
  if (!allowed && !_realtimeStorm) {
    _realtimeStorm = true; violation('realtime', 'storm_detected', entry.count, entry.maxCount, true)
    setTimeout(() => { _realtimeStorm = false; _limits.delete(`rt:${key}`) }, 5_000)
  }
  return allowed
}
export function activateEmergencyStop(reason?: string): void {
  _emergencyStop = true; violation('emergency', reason ?? 'manual_stop', 1, 1, true)
}
export function clearEmergencyStop(): void      { _emergencyStop = false }
export function isEmergencyStop(): boolean      { return _emergencyStop    }
export function isBroadcastThrottled(): boolean { return _broadcastThrottle }
export function isRealtimeStorm(): boolean      { return _realtimeStorm    }
export function getSafeguardSnapshot(): SafeguardSnapshot {
  return {
    timestamp: new Date().toISOString(), emergencyStop: _emergencyStop,
    rateLimits: Array.from(_limits.values()), violations: [..._violations].slice(-20),
    broadcastThrottle: _broadcastThrottle, realtimeStormDetected: _realtimeStorm,
  }
}
export function getViolations(): SafeguardViolation[] { return [..._violations] }
export function resetSafeguards(): void {
  _emergencyStop = false; _broadcastThrottle = false; _realtimeStorm = false
  _limits.clear(); _violations.length = 0
}
